<script setup lang="tsx">
const newPasswordModel = defineModel("newPassword", { default: "" });
const confirmPasswordModel = defineModel("confirmPassword", { default: "" });

const newPasswordContainsUppercase = ref(false);
const newPasswordContainsLowercase = ref(false);
const newPasswordContainsNumber = ref(false);
const newPasswordContainsSpecialChar = ref(false);
const newPasswordIsAtLeastEightCharsLong = ref(false);
const newPasswordsMatch = ref(false);

const allPasswordRequirementsMet = computed(() => {
  return (
    newPasswordContainsUppercase.value &&
    newPasswordContainsLowercase.value &&
    (newPasswordContainsNumber.value || newPasswordContainsSpecialChar.value) &&
    newPasswordIsAtLeastEightCharsLong.value &&
    newPasswordsMatch.value
  );
});

function checkPasswordStrength() {
  const password = newPasswordModel.value;

  newPasswordContainsUppercase.value = new RegExp("[A-Z]").test(password);
  newPasswordContainsLowercase.value = new RegExp("[a-z]").test(password);
  newPasswordContainsNumber.value = new RegExp("[0-9]").test(password);
  newPasswordContainsSpecialChar.value = new RegExp("[^A-Za-z 0-9]").test(
    password
  );
  newPasswordIsAtLeastEightCharsLong.value = password.length >= 8;
}

function checkIfPasswordsMatch() {
  if (
    newPasswordContainsUppercase.value ||
    newPasswordContainsLowercase.value ||
    newPasswordContainsNumber.value ||
    newPasswordContainsSpecialChar.value ||
    newPasswordIsAtLeastEightCharsLong.value
  ) {
    newPasswordsMatch.value =
      newPasswordModel.value == confirmPasswordModel.value;
  }
}

watch(
  () => newPasswordModel.value,
  (newValue) => {
    checkPasswordStrength();
  }
);

watch(
  () => confirmPasswordModel.value,
  (newValue) => {
    checkIfPasswordsMatch();
  }
);

defineExpose({
  allPasswordRequirementsMet,
  checkPasswordStrength,
  checkIfPasswordsMatch,
});
</script>

<template>
  <div class="flex flex-col gap-2 mt-4 text-xs">
    <div class="flex gap-1 items-center">
      <UIcon
        class="transition-all"
        :class="
          newPasswordIsAtLeastEightCharsLong
            ? 'text-primary-400'
            : 'text-red-500'
        "
        :name="
          newPasswordIsAtLeastEightCharsLong
            ? 'i-heroicons-check-16-solid'
            : 'i-heroicons-x-mark-16-solid'
        "
      />
      <span
        :class="
          newPasswordIsAtLeastEightCharsLong
            ? 'text-gray-700 dark:text-gray-200 transition-all'
            : 'text-gray-500 transition-all'
        "
        >{{ $t("profile.change_password_form.requirement.min_char") }}</span
      >
    </div>
    <div class="flex gap-1 items-center">
      <UIcon
        class="transition-all"
        :class="
          newPasswordContainsLowercase ? 'text-primary-400' : 'text-red-500'
        "
        :name="
          newPasswordContainsLowercase
            ? 'i-heroicons-check-16-solid'
            : 'i-heroicons-x-mark-16-solid'
        "
      />
      <span
        :class="
          newPasswordContainsLowercase
            ? 'text-gray-700 dark:text-gray-200 transition-all'
            : 'text-gray-500 transition-all'
        "
        >{{
          $t("profile.change_password_form.requirement.one_small_char")
        }}</span
      >
    </div>
    <div class="flex gap-1 items-center">
      <UIcon
        class="transition-all"
        :class="
          newPasswordContainsUppercase ? 'text-primary-400' : 'text-red-500'
        "
        :name="
          newPasswordContainsUppercase
            ? 'i-heroicons-check-16-solid'
            : 'i-heroicons-x-mark-16-solid'
        "
      />

      <span
        :class="
          newPasswordContainsUppercase
            ? 'text-gray-700 dark:text-gray-200 transition-all'
            : 'text-gray-500 transition-all'
        "
        >{{
          $t("profile.change_password_form.requirement.one_capital_char")
        }}</span
      >
    </div>
    <div class="flex gap-1 items-center">
      <UIcon
        class="transition-all"
        :class="
          newPasswordContainsNumber || newPasswordContainsSpecialChar
            ? 'text-primary-400'
            : 'text-red-500'
        "
        :name="
          newPasswordContainsNumber || newPasswordContainsSpecialChar
            ? 'i-heroicons-check-16-solid'
            : 'i-heroicons-x-mark-16-solid'
        "
      />
      <span
        :class="
          newPasswordContainsNumber || newPasswordContainsSpecialChar
            ? 'text-gray-700 dark:text-gray-200 transition-all'
            : 'text-gray-500 transition-all'
        "
      >
        {{ $t("profile.change_password_form.requirement.special_char") }}</span
      >
    </div>
    <div class="flex gap-1 items-center">
      <UIcon
        class="transition-all"
        :class="newPasswordsMatch ? 'text-primary-400' : 'text-red-500'"
        :name="
          newPasswordsMatch
            ? 'i-heroicons-check-16-solid'
            : 'i-heroicons-x-mark-16-solid'
        "
      />
      <span
        :class="
          newPasswordsMatch
            ? 'text-gray-700 dark:text-gray-200 transition-all'
            : 'text-gray-500 transition-all'
        "
      >
        {{
          $t("profile.change_password_form.requirement.passwords_match")
        }}</span
      >
    </div>
  </div>
</template>
